import { ColumnMetadata, ColumnTypes } from "~/components/DataGrid";
import { AssetColumns } from "~/modules/assets/types";
import { TokenRowData } from "~/modules/assets/components/AssetTable/types";
import { AssetTableRowData } from "~/modules/assets/components/AssetTable/types";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "~/lib/utils";
import { Link } from "~/components/Link";
import { getAssetURL } from "~/utils/getAssetURL";
import { ImageLoader } from "~/components/ImageLoader";
import { RWA_COIN_ICON } from "~/consts";
import { AssetUserEvents } from "~/modules/analytics/events/assets";
import { Emdash } from "~/components/Punctuation";
import { TokenNetworkLink } from "./TokenNetworkLink";
import { CatalogReferenceDataSlugs } from "~/modules/catalog/types";

// // // //

export const ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: (
        <>
            {/* ENHANCEMENT - add a "sublabel" or "subtitle" field to the ColumnMetadata interface? */}
            {/* Let's try this even though its inconsistent? */}
            <p>Name</p>
            <p className="text-xs">Ticker</p>
        </>
    ),
    slug: AssetColumns.name,
    sortsOn: AssetColumns.name,
    type: ColumnTypes.string,
    width: 300,
    sticky: {
        left: 0,
        isLast: true,
    },
    render: (item) => {
        if (item.isAsset) {
            const name = item.name;

            // NOTE - we include h-11 here so column heights don't "jump" when using the ColumnSwitcher component
            return (
                <div className="flex items-center justify-start select-none h-11">
                    <button
                        className={cn(
                            "mr-2 hover:bg-slate-200 transition-colors rounded-sm p-1",
                            {
                                "invisible pointer-events-none":
                                    item.tokens.length < 2,
                            },
                        )}
                        onClick={() => {
                            if (!item.onExpand) {
                                return;
                            }
                            item.onExpand();
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faChevronRight}
                            className={cn(
                                "float-left w-3 h-3 transition-transform",
                                {
                                    "rotate-90": item.expanded,
                                },
                            )}
                        />
                    </button>
                    <div className="flex items-start flex-col justify-center w-full overflow-hidden">
                        <Link
                            href={getAssetURL({ asset: item })}
                            variant="default"
                            className="w-full flex items-center gap-1.5"
                            analyticsLabel={AssetUserEvents.ClickIndividualLink}
                        >
                            <ImageLoader
                                src={item.icon_url || RWA_COIN_ICON}
                                alt={`${item.name} Icon`}
                                fallbackSrc={RWA_COIN_ICON}
                                className="h-5 w-5 min-w-5 rounded-full"
                            />
                            <span className="dg-overflow">{name}</span>
                        </Link>

                        <span className="font-light text-xs mt-0.5 text-muted-foreground dg-overflow font-mono">
                            {item.ticker ? `${item.ticker}` : <Emdash />}
                        </span>
                    </div>
                </div>
            );
        }

        const token = item as TokenRowData;
        return <TokenNetworkLink token={token} />;
    },
};

// // // //

export const ASSET_NAME_AND_TICKER_COLUMN_NON_EXPANDABLE: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: (
        <>
            {/* ENHANCEMENT - add a "sublabel" or "subtitle" field to the ColumnMetadata interface? */}
            {/* Let's try this even though its inconsistent? */}
            <p>Name</p>
            <p className="text-xs">Ticker</p>
        </>
    ),
    slug: AssetColumns.name,
    sortsOn: AssetColumns.name,
    type: ColumnTypes.string,
    width: 375,
    sticky: {
        left: 0,
        isLast: true,
    },
    render: (item) => {
        if (item.isAsset) {
            const name = item.name;

            // NOTE - we include h-11 here so column heights don't "jump" when using the ColumnSwitcher component
            return (
                <div className="flex items-start flex-col justify-center w-full overflow-hidden">
                    <Link
                        href={getAssetURL({ asset: item })}
                        variant="default"
                        className="w-full flex items-center gap-1.5"
                        analyticsLabel={AssetUserEvents.ClickIndividualLink}
                    >
                        <ImageLoader
                            src={item.icon_url || RWA_COIN_ICON}
                            alt={`${item.name} Icon`}
                            fallbackSrc={RWA_COIN_ICON}
                            className="h-5 w-5 min-w-5 rounded-full"
                        />
                        <span className="dg-overflow">{name}</span>
                    </Link>

                    <span className="font-light text-xs mt-0.5 text-muted-foreground dg-overflow font-mono">
                        {item.ticker ? `${item.ticker}` : <Emdash />}
                    </span>
                </div>
            );
        }

        // Return null for non-asset items (not expandable means this is never shown)
        return null;
    },
};

// // // //

export const NAME_COLUMN_INLINE: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Name",
    slug: AssetColumns.name,
    tooltip: CatalogReferenceDataSlugs.name,
    sortsOn: AssetColumns.name,
    type: ColumnTypes.string,
    width: 375,
    sticky: {
        left: 0,
        isLast: true,
    },
    render: (item) => {
        if (item.isAsset) {
            return <div className="flex items-center">{item.name}</div>;
        }

        return <Emdash />;
    },
};
